/**
 * Tabla de tareas de revision (la cola). La comparten la Bandeja y el Tablero del revisor.
 * Todas las acciones son REALES del backend y este decide si proceden (otra persona la tomo primero, conflicto de
 * intereses, el asignado no puede revisar esa unidad...); su motivo se muestra traducido (lib/errors.ts).
 *  - Revisor: Tomar / Revisar / Liberar la suya.
 *  - Administrador (`review.assign`): ademas Asignar a otra persona, cambiar la Prioridad (lo que recalcula el plazo:
 *    el reloj corre desde la apertura de la tarea) y Liberar la de cualquiera.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { hasAnywhere } from '../auth/access';
import {
  assignReviewTask,
  claimReviewTask,
  fetchRoleAssignments,
  releaseReviewTask,
  setReviewPriority,
  type ReviewPriority,
  type ReviewQueueRow,
} from '../lib/queries';
import { errorMessage } from '../lib/errors';
import { ageLabel, dueLabel, PRIORITIES, PRIORITY_LABEL, PRIORITY_PILL, priorityOptionLabel, reviewerCandidates } from '../lib/reviewQueue';

export function ReviewQueueTable({ rows, emptyText }: { rows: ReviewQueueRow[]; emptyText: string }) {
  const { authz, access } = useAuth();
  const qc = useQueryClient();
  const canAssign = hasAnywhere(access, 'review.assign');
  const [error, setError] = useState<string | null>(null);

  // Solo quien asigna necesita la lista de posibles revisores (la da la consola de roles).
  const rolesQ = useQuery({ queryKey: ['admin', 'role-assignments'], queryFn: fetchRoleAssignments, enabled: canAssign });
  const candidates = reviewerCandidates(rolesQ.data ?? []);

  // Un solo mensaje de error, el de la ultima accion: no se arrastra el de una accion anterior.
  const opts = {
    onMutate: () => setError(null),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['review'] }),
    onError: (e: unknown) => setError(errorMessage(e)),
  };
  const claim = useMutation({ mutationFn: claimReviewTask, ...opts });
  const release = useMutation({ mutationFn: releaseReviewTask, ...opts });
  const assign = useMutation({ mutationFn: ({ taskId, to }: { taskId: string; to: string }) => assignReviewTask(taskId, to), ...opts });
  const priority = useMutation({ mutationFn: ({ taskId, p }: { taskId: string; p: ReviewPriority }) => setReviewPriority(taskId, p), ...opts });

  if (rows.length === 0) {
    return (
      <p className="muted" style={{ fontSize: 12.5 }}>
        {emptyText}
      </p>
    );
  }

  return (
    <>
      {error && (
        <div className="callout callout--crit" style={{ marginBottom: 8 }}>
          {error}
        </div>
      )}
      <div className="tablewrap" style={{ border: 0 }}>
        <table className="data">
          <thead>
            <tr>
              <th>Código</th>
              <th>Actividad</th>
              <th>Unidad</th>
              <th>Prioridad</th>
              <th>Plazo</th>
              <th>Asignada a</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const mine = r.assignee_id === authz.user_id;
              const busy =
                (claim.isPending && claim.variables === r.task_id) ||
                (release.isPending && release.variables === r.task_id) ||
                (assign.isPending && assign.variables?.taskId === r.task_id) ||
                (priority.isPending && priority.variables?.taskId === r.task_id);
              return (
                <tr key={r.task_id}>
                  <td className="mono">{r.ref_code ?? '—'}</td>
                  <td>
                    <Link to={`/actividades/${r.activity_id}`}>{r.title}</Link>
                    {r.cycle_no > 1 && (
                      <span className="muted" style={{ fontSize: 11.5, marginLeft: 6 }} title="Vuelve a revisión tras una corrección">
                        ciclo {r.cycle_no}
                      </span>
                    )}
                    <div className="muted" style={{ fontSize: 11.5 }}>
                      en cola {ageLabel(r.age_hours)}
                    </div>
                  </td>
                  <td>{r.unit_name}</td>
                  <td>
                    {canAssign ? (
                      <select
                        aria-label={`Prioridad de ${r.ref_code ?? r.title}`}
                        title="Cambiar la prioridad recalcula el plazo desde que la tarea entró a la cola"
                        value={r.priority}
                        disabled={busy}
                        onChange={(e) => priority.mutate({ taskId: r.task_id, p: Number(e.target.value) as ReviewPriority })}
                      >
                        {PRIORITIES.map((p) => (
                          <option key={p} value={p}>
                            {priorityOptionLabel(p)}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className={`pill ${PRIORITY_PILL[r.priority]}`}>{PRIORITY_LABEL[r.priority]}</span>
                    )}
                  </td>
                  <td>
                    <span className={`pill ${r.overdue ? 'pill--critical' : 'pill--neutral'}`}>{dueLabel(r.hours_to_due)}</span>
                  </td>
                  <td>
                    {canAssign ? (
                      <select
                        aria-label={`Asignar la revisión de ${r.ref_code ?? r.title}`}
                        value={r.assignee_id ?? ''}
                        disabled={busy || rolesQ.isLoading}
                        onChange={(e) => e.target.value && assign.mutate({ taskId: r.task_id, to: e.target.value })}
                      >
                        <option value="" disabled>
                          Sin asignar
                        </option>
                        {candidates.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.id === authz.user_id ? `${c.email} (usted)` : c.email}
                          </option>
                        ))}
                      </select>
                    ) : mine ? (
                      'Usted'
                    ) : (
                      (r.assignee_email ?? <span className="muted">Sin asignar</span>)
                    )}
                  </td>
                  <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                    {!r.assignee_id && (
                      <button className="btn btn--primary" disabled={busy} onClick={() => claim.mutate(r.task_id)}>
                        {busy ? 'Un momento…' : 'Tomar'}
                      </button>
                    )}
                    {mine && (
                      <Link className="btn btn--primary" to={`/actividades/${r.activity_id}`}>
                        Revisar
                      </Link>
                    )}{' '}
                    {r.assignee_id && (mine || canAssign) && (
                      <button
                        className="btn btn--ghost"
                        disabled={busy}
                        title={mine ? undefined : 'Devolver la tarea a la cola'}
                        onClick={() => release.mutate(r.task_id)}
                      >
                        Liberar
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
